import { Component, Element, h, Host, Listen } from '@stencil/core';
import { ScrollRecord } from './scroll-record';
import { Throttle } from '../../utils/throttle';

@Component({
  tag: 'me-header',
  styleUrl: 'me-header.scss',
  shadow: false,
})
export class MeHeader {

  @Element() host: HTMLMeNavbarElement;

  private readonly scrollRecord = new ScrollRecord();
  private readonly throttle = new Throttle(200);

  private sticky?: HTMLMeStickyElement;
  private navbar?: HTMLMeNavbarElement;

  @Listen('scroll', { target: 'window' })
  handleScroll() {
    this.updateScrollStyles();
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.updateScrollStyles();
  }

  /**
   * Resets sticky container height on `hashchange` events
   *
   * This is a band-aid fix for scroll anchor clicks with small distances.
   * It makes sure that the navbar does not cover the scroll target.
   * However, it does not work on subsequent clicks on the same anchor.
   * (`hashchange` gets only emitted on actual changes …)
   *
   * This solution should be good enough though … (KISS)
   */
  @Listen('hashchange', { target: 'window' })
  async handleHashchange() {
    await this.sticky?.disable();
    this.scrollRecord.update();
  }

  componentWillLoad() {
    this.navbar = this.host.querySelector(':scope > me-navbar');
  }

  render() {
    return (
      <Host class="me-header">
        <me-sticky ref={el => this.sticky = el}>
          <slot name="navbar" />
        </me-sticky>
        <slot name="menu" />
      </Host>
    );
  }

  private updateScrollStyles() {
    if (!this.sticky || !this.navbar) {
      return;
    }

    requestAnimationFrame(async () => {
      // Scroll sticky navbar out of view when the user scrolls down.
      if (await this.sticky.shouldScrollOut(this.scrollRecord.direction)) {
        await this.sticky.scrollOut();
        return;
      }

      // Disable sticky behavior after the navbar has scrolled out.
      // Navbar will remain at its origin until it should slide in again.
      if (await this.sticky.shouldBeOff(this.scrollRecord.direction)) {
        await this.sticky.disable();
        return;
      }

      // Slide in the navbar when the user scrolls up quickly.
      if (await this.navbar.shouldSlideIn(this.scrollRecord.speed, this.sticky.height)) {
        await this.navbar.slideOut(false);
        await this.sticky.enable();
        await this.navbar.slideIn();
      }

      this.throttle.execute(() => {
        this.scrollRecord.update();
      });
    });
  }

}
